import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { assetService } from "./asset.service";
import type {
	AssetFileParams,
	AssetInstanceListParams,
	AssetNoteParams,
	AssetParams,
	AssetTypesAllParams,
	AssetTypesListParams,
	AssetTypesQueryOptionsInput,
} from "./asset.types";
import { normalizeAssetTypesQueryOptions } from "./asset.types";

export const assetKeys = {
	all: ["asset"] as const,
	assets: () => [...assetKeys.all, "assets"] as const,
	assetsDetail: (id: string, organizationId?: string) =>
		[...assetKeys.assets(), "detail", id, organizationId] as const,
	types: () => [...assetKeys.all, "asset-types"] as const,
	typeDetail: (id: string, organizationId?: string) =>
		[...assetKeys.types(), "detail", id, organizationId] as const,
	geometryTypes: () => [...assetKeys.all, "geometry-types"] as const,
	geometrySources: () => [...assetKeys.all, "geometry-sources"] as const,
	gpsMethods: () => [...assetKeys.all, "gps-methods"] as const,
	typesAll: (
		params?:
			| AssetTypesAllParams
			| AssetTypesListParams
			| AssetTypesQueryOptionsInput,
	) => [...assetKeys.types(), "all", params] as const,
	typesCount: (params?: AssetTypesListParams) =>
		[...assetKeys.types(), "count", params] as const,
	assetsCount: (params?: AssetParams) =>
		[...assetKeys.types(), "assets-count", params] as const,
	assetsFeatures: (start: number, count: number, params?: AssetParams) =>
		[...assetKeys.all, "assets-features", start, count, params] as const,
	typesPage: (start: number, count: number, params?: AssetTypesListParams) =>
		[...assetKeys.types(), "page", start, count, params] as const,
	assetTypeHistory: (id: string) =>
		[...assetKeys.types(), "history", id] as const,
	assetTypeHistoryCount: (id: string) =>
		[...assetKeys.assetTypeHistory(id), "count"] as const,
	assetTypeHistoryList: (id: string, start: number, count: number) =>
		[...assetKeys.assetTypeHistory(id), "list", start, count] as const,
};

export const assetInstanceKeys = {
	all: ["asset-instance"] as const,
	lists: () => [...assetInstanceKeys.all, "list"] as const,
	list: (start: number, count: number, params?: AssetInstanceListParams) =>
		[...assetInstanceKeys.lists(), start, count, params] as const,
	counts: () => [...assetInstanceKeys.all, "count"] as const,
	count: (params?: AssetParams) =>
		[...assetInstanceKeys.counts(), params] as const,
	details: () => [...assetInstanceKeys.all, "detail"] as const,
	detail: (assetId: string, organizationId?: string) =>
		[...assetInstanceKeys.details(), assetId, organizationId] as const,
	history: (assetId: string) =>
		[...assetInstanceKeys.all, "history", assetId] as const,
	historyCount: (assetId: string) =>
		[...assetInstanceKeys.history(assetId), "count"] as const,
	historyList: (assetId: string, start: number, count: number) =>
		[...assetInstanceKeys.history(assetId), "list", start, count] as const,
	files: (assetId: string) =>
		[...assetInstanceKeys.all, "files", assetId] as const,
	filesCount: (assetId: string, params?: AssetFileParams) =>
		[...assetInstanceKeys.files(assetId), "count", params] as const,
	filesList: (
		assetId: string,
		start: number,
		count: number,
		params?: AssetFileParams,
	) =>
		[
			...assetInstanceKeys.files(assetId),
			"list",
			start,
			count,
			params,
		] as const,
	fileContent: (assetId: string, fileId: string) =>
		[...assetInstanceKeys.files(assetId), "content", fileId] as const,
	fileThumbnail: (assetId: string, fileId: string) =>
		[...assetInstanceKeys.files(assetId), "thumbnail", fileId] as const,
	notes: (assetId: string) =>
		[...assetInstanceKeys.all, "notes", assetId] as const,
	notesCount: (assetId: string, params?: AssetNoteParams) =>
		[...assetInstanceKeys.notes(assetId), "count", params] as const,
	notesList: (
		assetId: string,
		start: number,
		count: number,
		params?: AssetNoteParams,
	) =>
		[
			...assetInstanceKeys.notes(assetId),
			"list",
			start,
			count,
			params,
		] as const,
	validStatuses: (assetId: string) =>
		[...assetInstanceKeys.all, assetId, "valid-statuses"] as const,
	records: (assetId: string) =>
		[...assetInstanceKeys.all, "records", assetId] as const,
};

function assetTypesAllQueryOptions(input?: AssetTypesQueryOptionsInput) {
	const params = normalizeAssetTypesQueryOptions(input);
	return queryOptions({
		queryKey: assetKeys.typesAll(params),
		queryFn: () => assetService.getAssetTypes(params as AssetTypesAllParams),
		staleTime: 10 * 60 * 1000,
		gcTime: 30 * 60 * 1000,
	});
}

export const assetQueryOptions = {
	detail: (id: string, options?: { organizationId?: string }) =>
		queryOptions({
			queryKey: assetKeys.assetsDetail(id, options?.organizationId),
			queryFn: () => assetService.getAsset(id, options),
			staleTime: 5 * 60 * 1000,
			gcTime: 10 * 60 * 1000,
			enabled: !!id && id.length > 0,
		}),

	typeDetail: (id: string, options?: { organizationId?: string }) =>
		queryOptions({
			queryKey: assetKeys.typeDetail(id, options?.organizationId),
			queryFn: () => assetService.getAssetTypeDetail(id, options),
			staleTime: 60 * 1000,
			gcTime: 5 * 60 * 1000,
		}),

	typesAll: (input?: AssetTypesQueryOptionsInput) =>
		assetTypesAllQueryOptions(input),

	typesCount: (input?: AssetTypesQueryOptionsInput) => {
		const params = normalizeAssetTypesQueryOptions(input);
		return queryOptions({
			queryKey: assetKeys.typesCount(params),
			queryFn: () => assetService.getAssetTypesCount(params),
			staleTime: 0,
			gcTime: 0,
		});
	},

	assetsCount: (params?: AssetParams) =>
		queryOptions({
			queryKey: assetKeys.assetsCount(params),
			queryFn: ({ signal }) => assetService.getAssetsCount(params, { signal }),
			staleTime: 0,
			gcTime: 5 * 60 * 1000,
		}),

	features: (start: number = 1, count: number = 10, params?: AssetParams) =>
		queryOptions({
			queryKey: assetKeys.assetsFeatures(start, count, params),
			queryFn: () => assetService.getAssetsFeatures(start, count, params),
			staleTime: 5 * 60 * 1000,
			gcTime: 10 * 60 * 1000,
		}),

	typesPage: (
		start: number,
		count: number,
		input?: AssetTypesQueryOptionsInput,
	) => {
		const params = normalizeAssetTypesQueryOptions(input);
		return queryOptions({
			queryKey: assetKeys.typesPage(start, count, params),
			queryFn: () => assetService.getAssetTypesPage(start, count, params),
			staleTime: 0,
			gcTime: 0,
		});
	},

	geometryTypes: () =>
		queryOptions({
			queryKey: assetKeys.geometryTypes(),
			queryFn: () => assetService.getAssetGeometryTypes(),
			staleTime: 30 * 60 * 1000,
			gcTime: 60 * 60 * 1000,
		}),

	geometrySources: () =>
		queryOptions({
			queryKey: assetKeys.geometrySources(),
			queryFn: () => assetService.getGeometrySources(),
			staleTime: 30 * 60 * 1000,
			gcTime: 60 * 60 * 1000,
		}),

	gpsMethods: () =>
		queryOptions({
			queryKey: assetKeys.gpsMethods(),
			queryFn: () => assetService.getGpsMethods(),
			staleTime: 30 * 60 * 1000,
			gcTime: 60 * 60 * 1000,
		}),

	assetTypeHistory: {
		count: (assetTypeId: string, organizationId?: string) =>
			queryOptions({
				queryKey: [
					...assetKeys.assetTypeHistoryCount(assetTypeId),
					organizationId,
				],
				queryFn: () =>
					assetService.getAssetTypeHistoryCount(assetTypeId, organizationId),
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				enabled: !!assetTypeId && assetTypeId.length > 0,
			}),

		infinite: (
			assetTypeId: string,
			pageSize: number = 10,
			totalCount: number,
			organizationId?: string,
		) =>
			infiniteQueryOptions({
				queryKey: [
					...assetKeys.assetTypeHistoryList(assetTypeId, 1, pageSize),
					"infinite",
					totalCount,
					organizationId,
				],
				queryFn: ({ pageParam }) =>
					assetService.getAssetTypeHistory(
						assetTypeId,
						pageParam,
						pageSize,
						organizationId,
					),
				initialPageParam: totalCount
					? Math.max(1, totalCount - pageSize + 1)
					: 1,
				getNextPageParam: (_lastPage, _allPages, lastPageParam) => {
					if (lastPageParam <= 1) return undefined;
					return Math.max(1, lastPageParam - pageSize);
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (!totalCount || firstPageParam >= totalCount - pageSize + 1) {
						return undefined;
					}
					return firstPageParam + pageSize;
				},
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				enabled:
					!!assetTypeId && assetTypeId.length > 0 && totalCount !== undefined,
			}),
	},
};

export const assetInstanceQueryOptions = {
	list: (start: number, count: number, params?: AssetInstanceListParams) =>
		queryOptions({
			queryKey: assetInstanceKeys.list(start, count, params),
			queryFn: () => assetService.listAssets(start, count, params),
			staleTime: 0,
		}),
	count: (params?: AssetParams) =>
		queryOptions({
			queryKey: assetInstanceKeys.count(params),
			queryFn: ({ signal }) => assetService.getAssetsCount(params, { signal }),
			staleTime: 0,
			gcTime: 0,
		}),
	detail: (assetId: string, options?: { organizationId?: string }) =>
		queryOptions({
			queryKey: assetInstanceKeys.detail(assetId, options?.organizationId),
			queryFn: () => assetService.getAsset(assetId, options),
			enabled: !!assetId && assetId.length > 0,
		}),
	validStatuses: (assetId: string) =>
		queryOptions({
			queryKey: assetInstanceKeys.validStatuses(assetId),
			queryFn: () => assetService.getAssetValidStatuses(assetId),
			staleTime: 10 * 60 * 1000,
			enabled: !!assetId && assetId.length > 0,
		}),
	history: {
		count: (assetId: string) =>
			queryOptions({
				queryKey: assetInstanceKeys.historyCount(assetId),
				queryFn: () => assetService.getAssetHistoryCount(assetId),
			}),
		/**
		 * Infinite query for asset history (newest first).
		 * @param totalCount - Required for reverse pagination
		 */
		infinite: (assetId: string, pageSize: number = 10, totalCount: number) =>
			infiniteQueryOptions({
				queryKey: [
					...assetInstanceKeys.historyList(assetId, 1, pageSize),
					"infinite",
					totalCount,
				],
				queryFn: ({ pageParam }) =>
					assetService.getAssetHistory(assetId, pageParam, pageSize),
				initialPageParam: totalCount
					? Math.max(1, totalCount - pageSize + 1)
					: 1,
				getNextPageParam: (_lastPage, _allPages, lastPageParam) => {
					if (lastPageParam <= 1) return undefined;
					return Math.max(1, lastPageParam - pageSize);
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (!totalCount || firstPageParam >= totalCount - pageSize + 1) {
						return undefined;
					}
					return firstPageParam + pageSize;
				},
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				enabled: !!assetId && totalCount !== undefined,
			}),
	},
	files: {
		count: (
			assetId: string,
			params?: AssetFileParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: assetInstanceKeys.filesCount(assetId, params),
				queryFn: () =>
					assetService.getAssetFilesCount(assetId, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!assetId && assetId.length > 0,
			}),
		list: (
			assetId: string,
			start: number = 1,
			count: number = 50,
			params?: AssetFileParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: assetInstanceKeys.filesList(assetId, start, count, params),
				queryFn: () =>
					assetService.getAssetFiles(assetId, start, count, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!assetId && assetId.length > 0,
			}),
		content: (
			assetId: string,
			fileId: string,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: assetInstanceKeys.fileContent(assetId, fileId),
				queryFn: () => assetService.getAssetFile(assetId, fileId, options),
				staleTime: 5 * 60 * 1000,
				gcTime: 30 * 60 * 1000,
				enabled: !!assetId && !!fileId,
			}),
		thumbnail: (
			assetId: string,
			fileId: string,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: assetInstanceKeys.fileThumbnail(assetId, fileId),
				queryFn: async () => {
					try {
						return await assetService.getAssetFileThumbnail(
							assetId,
							fileId,
							options,
						);
					} catch {
						return null;
					}
				},
				staleTime: 5 * 60 * 1000,
				gcTime: 30 * 60 * 1000,
				enabled: !!assetId && !!fileId,
				retry: false,
			}),
	},
	notes: {
		count: (
			assetId: string,
			params?: AssetNoteParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: assetInstanceKeys.notesCount(assetId, params),
				queryFn: () =>
					assetService.getAssetNotesCount(assetId, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!assetId && assetId.length > 0,
			}),
		list: (
			assetId: string,
			start: number = 1,
			count: number = 50,
			params?: AssetNoteParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: assetInstanceKeys.notesList(assetId, start, count, params),
				queryFn: () =>
					assetService.getAssetNotes(assetId, start, count, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!assetId && assetId.length > 0,
			}),
	},
};

export const assetQueryKeys = assetKeys;
export const assetQueries = {
	assets: {
		detail: assetQueryOptions.detail,
	},
	types: {
		detail: assetQueryOptions.typeDetail,
		all: assetQueryOptions.typesAll,
		count: assetQueryOptions.typesCount,
		assetsCount: assetQueryOptions.assetsCount,
		page: assetQueryOptions.typesPage,
	},
	geometryTypes: assetQueryOptions.geometryTypes,
};
